<template>
    <div id="vehicle-panel" class="tiny-panel details-panel">
        <details-head
            v-if="truck"
            :truck="truck"
            :collapsed="false"
            @close="closePanel"
        />

        <details-body
            v-if="truck"
            :truck="truck"
            :collapsed="false"
            class="d-body details-desktop-body"

            @updateTruckTags="(tags) => updateTruckTags(tags, truck)"
            ref="details_body"
        />

        <div class="panel-tools d-flex flex-row justify-content-center" v-if="truck">
            <golia-button
                @click="goToAnalyze"
                variant="btn-fill dark-blue btn-circle"
                icon="fa-solid fa-fw fa-chart-mixed"
                v-b-tooltip.hover
                :title="__('analyze')"
            />
            <golia-button
                v-if="$can('use', 'fuel_stations') || $can('use', 'parkings')"
                @click="openPanel('searchResources')"
                variant="btn-fill dark-blue btn-circle"
                icon="fa-fw fa-search-poi"
                icon-weight="fa-kit"
            />
            <golia-button
                v-if="$can('use', 'planner')"
                @click="goToPlan"
                variant="btn-fill dark-blue btn-circle"
                icon="fa-solid fa-fw fa-route"
                v-b-tooltip.hover
                :title="__('plan')"
            />
            <golia-button
                v-if="$can('use', 'share_link')"
                v-b-modal.modal-vehicle-share-link
                variant="btn-fill dark-blue btn-circle"
                icon="fa-solid fa-fw fa-share"
            />
        </div>

        <b-modal
            v-if="truck"
            id="modal-vehicle-share-link"
            :title="__('sharedlinks.share_single_vehicle') + ' ' + truck.plate"
            size="lg"
            @cancel="closeShareModal"
        >
            <div class="create-link" v-show="!createdLink">
                <p class="mt-2 mb-3">{{ __('sharedlinks.start_expire_date_description') }}:</p>
                <div class="form-group">
                    <label>{{ __('sharedlinks.select_start_expire_date') }}</label>
                    <golia-date-picker v-model="shareLinkStart" :monthPanels="1" :specificTime="false" />
                </div>
            </div>

            <div class="preview-link" v-show="createdLink">
                <p class="my-2 p-2 text-white" :class="createdLink && createdLink.errors ? 'bg-danger' : 'bg-success'">
                    {{ createdLink && createdLink.errors ? __('sharedlinks.created_link_error') : __('sharedlinks.created_link_success') }}
                </p>
                <p class="mb-3 share-link-message" v-show="createdLink && !createdLink.errors">
                    {{ __('sharedlinks.service_description') }}:
                    <span class="d-block position-relative" v-if="createdLink && createdLink.data">
                        <b>
                            <router-link ref="sharedLink" target="_blank" :to="'/view-shared-link/' + createdLink.data.token">
                                {{ __('sharedlinks.link_preview') }} ({{ __('click_here') }})
                            </router-link>
                        </b>
                        <input type="text" v-on:focus="$event.target.select()" ref="sharedLinkInput" readonly :value="computedSharedLink" />
                        <button class="btn btn-sm btn-success" @click="copySharedLink">
                            <i class="fa-regular fa-copy"></i>
                            {{ copiedLink ? __('copied') : __('copy') }}
                        </button>
                    </span>
                    <golia-shareNetwork v-if="computedSharedLink" :sharedLink="computedSharedLink" :truckPlate="truck.plate" :bigButton="true" />
                </p>
            </div>

            <template #modal-footer="{ cancel }">
                <golia-button variant="btn-fill dark-blue" @click="cancel()" :label="createdLink ? __('close') : __('cancel')" />
                <golia-button variant="btn-fill success" v-show="createdLink" @click="createdLink = null"
                    :label="createdLink && createdLink.errors ? __('retry') : __('sharedlinks.create_link')" />
                <golia-button variant="btn-fill success" @click="createShareLink(truck.id)" v-show="!createdLink"
                    :label="__('sharedlinks.create_link')" :disabled="shareDifference <= 0" :loading="shareLoading" />
            </template>
        </b-modal>
    </div>
</template>

<script>
import DetailsHead from './DetailsPanel/DetailsHead.vue'
import DetailsBody from './DetailsPanel/DetailsBody.vue'

import { mapGetters, mapMutations } from 'vuex'
import truckPageGuard from '@/mixins/truckPageGuard'

export default {
    name: 'VehiclePanel',
    mixins: [truckPageGuard],
    components: { DetailsHead, DetailsBody },
    data() {
        return {
            shareLinkStart: {
                start: this.moment().startOf('day'),
                end: this.moment().endOf('day')
            },
            shareLoading: false,
            createdLink: null,
            copiedLink: false
        }
    },
    computed: {
        computedSharedLink() {
            if (this.createdLink && this.createdLink.data) {
                return this.$path_join(
                    window.location.origin,
                    this.$router.resolve({ name: 'viewvehicle.index', params: { token: this.createdLink.data.token } }).href
                )
            }
            return null
        },
        shareDifference() {
            return this.shareLinkStart ? this.shareLinkStart.end.diff(this.shareLinkStart.start, 'hours') : 0
        },
        startToSend() {
            return this.shareLinkStart.start.clone().utc().toISOString()
        },
        expireToSend() {
            return this.shareLinkStart.end.clone().utc().toISOString()
        },
        ...mapGetters({
            searchResourcesOpen: 'searchResourcesOpen'
        })
    },
    methods: {
        closePanel() {
            this.$store.commit('cleanDriversPlafondCache')
            if (this.searchResourcesOpen) {
                this.$store.commit('closePanel', 'searchResources')
            }
            // Mixin handles unfocus + expand main + push to index.
            this.unfocusAndGoToIndex()
        },
        updateTruckTags(tags, truck) {
            this.$store.commit('updateTruckProperty', { truck, property: 'tags', value: tags })
        },
        // Truck is already focused on this page; just navigate to its sub-routes
        // (same targets as the list-row analyze/plan buttons).
        goToAnalyze() {
            this.$router.push({ name: 'geofleet.vehicle_analyze', params: { id: this.truck.plate } })
        },
        goToPlan() {
            this.$router.push({ name: 'geofleet.vehicle_plan', params: { id: this.truck.plate } })
        },
        copySharedLink() {
            this.copiedLink = true
            const input = this.$refs.sharedLinkInput
            input.select()
            document.execCommand('copy')
            setTimeout(() => { this.copiedLink = false }, 2000)
        },
        createShareLink(truckId) {
            this.shareLoading = true
            this.$fetch(this.$env.GOLIA_API + '/sharelink', {
                method: 'POST',
                body: JSON.stringify({
                    truck_id: truckId,
                    start_at: this.startToSend,
                    expire_at: this.expireToSend,
                    tz: this.$authProcess.getParams('tz')
                })
            })
                .then(res => res.json())
                .then(res => {
                    this.shareLoading = false
                    this.createdLink = res
                    this.$store.commit('addLink', res.data)
                })
        },
        closeShareModal() {
            this.createdLink = null
            this.shareLinkStart = {
                start: this.moment().startOf('day'),
                end: this.moment().endOf('day')
            }
        },
        ...mapMutations({ openPanel: 'openPanel' })
    }
}
</script>

<style scoped lang="scss">
#vehicle-panel {
    // Match the original DetailsPanel width (340px) so the vehicle details
    // doesn't grow to fill the panel-box like the routed planner/analyze pages.
    flex: 0 0 340px;
    width: 340px;
    display: flex;
    flex-direction: column;

    .d-body {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        overflow-y: auto;
        flex: 1;
        margin-bottom: 15px;
    }

    .panel-tools {
        gap: 8px;
        padding-top: 8px;
    }
}
</style>
